import React from 'react';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';

/**
 * Keeps a failure inside the Finance tab inside the Finance tab.
 *
 * This tab reads more than its own endpoint: it pulls the client list and the
 * hub list from core-service masterdata for its two filters. Those are a
 * different service with a different uptime, and locally they are usually not
 * running at all — a 404 there is the normal case, not the exception.
 *
 * Without a boundary, any throw while rendering takes React's whole tree with
 * it: the operator loses the board and the per-rider recap too, over a dropdown
 * that only narrows a list. The money screens must survive their filters.
 *
 * Deliberately NOT a silent swallow. It shows what broke and offers a reload,
 * because a Finance queue that renders empty for an unstated reason is worse
 * than one that says it could not load — the first reads as "nothing to
 * verify".
 */
interface State {
  error: Error | null;
}

export class CodFinanceBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Kept on the console rather than sent anywhere: this app has no error
    // reporter, and a boundary that hides the stack from the next developer
    // trades one silent failure for another.
    // eslint-disable-next-line no-console
    console.error(
      '[CodFinancePanel] render failed',
      error,
      info.componentStack
    );
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex flex-col gap-3">
        <Alert status="error" size="lg">
          <div>
            <b>Tab Finance gagal dimuat.</b> Antrian dan tab lain tidak
            terpengaruh — muat ulang halaman untuk mencoba lagi.
            <div className="mt-1 font-mono text-xs opacity-80">
              {this.state.error.message}
            </div>
          </div>
        </Alert>
        <div>
          <Button
            style="stroke"
            size="sm"
            onClick={() => this.setState({ error: null })}
          >
            Coba lagi
          </Button>
        </div>
      </div>
    );
  }
}
